import {
  CreateProjectSchema,
  type ParsedCreateProject,
  type ParsedProject,
  type ParsedUpdateProject,
  ProjectSchema,
  UpdateProjectSchema,
} from '@rhia/protocol';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/lib/api';

const ProjectsArraySchema = z.array(ProjectSchema);

export function useProjects(workspaceId: string) {
  return useQuery<ParsedProject[], Error>({
    queryKey: ['workspaces', workspaceId, 'projects'],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/api/workspaces/${workspaceId}/projects`);
      return ProjectsArraySchema.parse(raw);
    },
    staleTime: 30_000,
  });
}

export function useProject(workspaceId: string, projectId: string) {
  return useQuery<ParsedProject, Error>({
    queryKey: ['workspaces', workspaceId, 'projects', projectId],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/api/workspaces/${workspaceId}/projects/${projectId}`);
      return ProjectSchema.parse(raw);
    },
    staleTime: 30_000,
  });
}

export function useCreateProject(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ParsedCreateProject) => {
      CreateProjectSchema.parse(input);
      const raw = await apiFetch<unknown>(`/api/workspaces/${workspaceId}/projects`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return ProjectSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'projects'] });
    },
  });
}

export function useUpdateProject(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, patch }: { projectId: string; patch: ParsedUpdateProject }) => {
      UpdateProjectSchema.parse(patch);
      const raw = await apiFetch<unknown>(`/api/workspaces/${workspaceId}/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      return ProjectSchema.parse(raw);
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'projects'] });
      qc.setQueryData(['workspaces', workspaceId, 'projects', updated.id], updated);
    },
  });
}

export function useDeleteProject(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      await apiFetch<unknown>(`/api/workspaces/${workspaceId}/projects/${projectId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'projects'] });
    },
  });
}
